'use client';

/**
 * QualityPanel - Data quality analysis for Obeya mode
 *
 * Uses klyde_intelligence OODA-L orchestrator for data quality synthesis.
 * Returns summary, urgency level, and recommendations with citations.
 *
 * @component_role Data Quality Analysis
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import {
  Loader2,
  XCircle,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  Lightbulb,
  ClipboardCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useViewportFast, useViewportSlow } from '@/lib/api/hooks';
import { useLayoutStore } from '@/lib/stores/layout-store';
import type { StrategyResult, HealthSynthesis, HealthRecommendation } from '@/lib/api/types';

interface QualityPanelProps {
  nodeId: string;
  projectId?: string | null;
}

export function QualityPanel({ nodeId, projectId }: QualityPanelProps) {
  // Use projectId if available (from graph context), otherwise use nodeId
  const targetProjectId = projectId || nodeId;
  const activeMode = useLayoutStore((s) => s.activeExplorerView);

  // Fast tier: Load project info immediately (Neo4j query only, <100ms)
  const { data: fastData } = useViewportFast(activeMode, targetProjectId);

  // Slow tier: Load synthesis in background (LLM call, 1-5s)
  // Uses cached context from fast tier to skip duplicate OBSERVE work
  const {
    data: slowData,
    isLoading: isSynthesisLoading,
    error,
    refetch,
  } = useViewportSlow(activeMode, fastData?.context_key);

  // Extract synthesis from slow tier inspector results
  // Strategy name is "data_quality_check", capability output key is "data_quality_synthesis"
  const qualityResult = slowData?.inspector?.data_quality_check as StrategyResult | undefined;
  const strategyResult = qualityResult;
  const synthesis = qualityResult?.decide_results?.data_quality_synthesis as HealthSynthesis | undefined;

  // Project info from fast tier context
  // project_context returns rfqs/pos arrays (not metadata.rfq_count)
  const projectContext = fastData?.context?.project_context as {
    found?: boolean;
    project?: { name?: string; semantic_id?: string };
    rfqs?: unknown[];
    pos?: unknown[];
  } | undefined;
  const projectName = projectContext?.project?.name;
  const projectSemanticId = projectContext?.project?.semantic_id;
  const rfqCount = projectContext?.rfqs?.length ?? 0;
  const poCount = projectContext?.pos?.length ?? 0;

  // Urgency icon helper
  const getUrgencyIcon = (urgency: string) => {
    switch (urgency) {
      case 'low':
        return <CheckCircle className="h-4 w-4 text-success" />;
      case 'medium':
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      case 'high':
        return <XCircle className="h-4 w-4 text-destructive" />;
      default:
        return <ClipboardCheck className="h-4 w-4 text-muted-foreground" />;
    }
  };

  // Priority badge color
  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'high':
        return 'bg-destructive/10 border-destructive/20 text-destructive';
      case 'medium':
        return 'bg-warning/10 border-warning/20 text-warning';
      case 'low':
        return 'bg-success/10 border-success/20 text-success';
      default:
        return 'bg-muted border-border';
    }
  };

  return (
    <div className="space-y-4 p-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-muted-foreground">DATA QUALITY</h4>
        <button
          onClick={() => refetch()}
          disabled={isSynthesisLoading}
          className="p-1.5 rounded hover:bg-accent transition-colors disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw className={cn('h-4 w-4 text-muted-foreground', isSynthesisLoading && 'animate-spin')} />
        </button>
      </div>

      {/* Project info (loads immediately) */}
      {projectName && (
        <div className="p-3 rounded-lg bg-card border border-border">
          <div className="font-medium">{projectName}</div>
          <div className="flex items-center gap-2 mt-2 text-xs">
            <span className="px-1.5 py-0.5 rounded bg-muted">{rfqCount} RFQs</span>
            <span className="px-1.5 py-0.5 rounded bg-muted">{poCount} POs</span>
          </div>
          {projectSemanticId && (
            <div className="text-xs text-muted-foreground mt-2 font-mono">{projectSemanticId}</div>
          )}
        </div>
      )}

      {/* Synthesis section (loads in background) */}
      {isSynthesisLoading ? (
        <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
          <Loader2 className="h-6 w-6 mb-2 animate-spin" />
          <p className="text-sm">Analyzing data quality...</p>
          <p className="text-xs mt-1">Running OODA-L decision loop</p>
        </div>
      ) : error || (qualityResult && qualityResult.success === false) ? (
        <div className="flex flex-col items-center justify-center py-4 text-center text-destructive">
          <XCircle className="h-6 w-6 mb-2" />
          <p className="text-sm">Failed to analyze data quality</p>
          <p className="text-xs mt-1">{(error as Error)?.message || (qualityResult as { errors?: string[] })?.errors?.[0]}</p>
        </div>
      ) : !synthesis ? (
        <div className="flex flex-col items-center justify-center py-4 text-center text-muted-foreground">
          <ClipboardCheck className="h-6 w-6 mb-2 opacity-50" />
          <p className="text-sm">No data quality analysis available</p>
        </div>
      ) : (
        <>
          {/* Urgency & Summary */}
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground mb-2">STATUS</h4>
            <div className={cn(
              "flex items-start gap-2 p-3 rounded-lg border",
              synthesis.urgency === 'low' && "bg-success/10 border-success/20",
              synthesis.urgency === 'medium' && "bg-warning/10 border-warning/20",
              synthesis.urgency === 'high' && "bg-destructive/10 border-destructive/20",
            )}>
              {getUrgencyIcon(synthesis.urgency)}
              <div>
                <div className="font-medium text-sm capitalize">{synthesis.urgency} Urgency</div>
                <div className="text-xs text-muted-foreground mt-0.5">{synthesis.summary}</div>
              </div>
            </div>
          </div>

          {/* Recommendations with citations */}
          {synthesis.recommendations?.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground mb-2">RECOMMENDATIONS</h4>
              <div className="space-y-2">
                {synthesis.recommendations.map((rec: HealthRecommendation, i: number) => (
                  <div
                    key={i}
                    className={cn(
                      "p-2 rounded border",
                      getPriorityColor(rec.priority)
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <Lightbulb className="h-4 w-4 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{rec.action}</div>
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <ChevronRight className="h-3 w-3" />
                          Based on: {rec.based_on}
                        </div>
                      </div>
                      {rec.priority && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-background/50 capitalize">
                          {rec.priority}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trust level footer */}
          {strategyResult && (
            <div className="text-xs text-muted-foreground pt-2 border-t border-border flex items-center gap-2">
              <span>Trust: {strategyResult.trust_level}</span>
              {(strategyResult.errors?.length ?? 0) > 0 && (
                <span className="text-destructive">({strategyResult.errors?.length} errors)</span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
